import { useState, useEffect, useRef, useCallback } from 'react';

// Inject the mic pulse + spin animations used by dictation controls (once per
// page load). Extracted from DeployIntakeForm so any tool can reuse the hook.
if (typeof document !== 'undefined' && !document.getElementById('lyric-dictation-styles')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'lyric-dictation-styles';
  styleEl.textContent = `
    @keyframes lyricMicPulse {
      0%   { box-shadow: 0 0 0 0 rgba(239, 83, 80, 0.65); }
      70%  { box-shadow: 0 0 0 10px rgba(239, 83, 80, 0);  }
      100% { box-shadow: 0 0 0 0 rgba(239, 83, 80, 0);    }
    }
    @keyframes lyricGearSpin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    .lyric-gear-spin {
      display: inline-block;
      animation: lyricGearSpin 2s linear infinite;
    }
    @keyframes lyricSpinDeploy {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleEl);
}

/**
 * Browser-native speech-to-text using the Web Speech API.
 * Returns { listening, supported, error, start, stop, toggle }.
 * `onResult(text, { isFinal })` fires for each interim/final transcript chunk.
 */
export default function useDictation({ onResult, lang = 'en-US' } = {}) {
  const Recognition =
    typeof window !== 'undefined'
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : null;
  const supported = Boolean(Recognition);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState('');
  const recognitionRef = useRef(null);
  const onResultRef = useRef(onResult);
  useEffect(() => { onResultRef.current = onResult; }, [onResult]);

  const stop = useCallback(() => {
    const rec = recognitionRef.current;
    if (rec) {
      try { rec.stop(); } catch { /* already stopped */ }
    }
    setListening(false);
  }, []);

  const start = useCallback(() => {
    if (!supported) {
      setError('Voice input is not supported in this browser. Try Chrome or Edge.');
      return;
    }
    if (recognitionRef.current) return; // already running
    setError('');
    const rec = new Recognition();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;

    rec.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) final += result[0].transcript;
        else interim += result[0].transcript;
      }
      if (final && onResultRef.current) onResultRef.current(final, { isFinal: true });
      if (interim && onResultRef.current) onResultRef.current(interim, { isFinal: false });
    };
    rec.onerror = (e) => {
      setError(e.error === 'not-allowed' ? 'Microphone access was denied.' : `Voice input error: ${e.error}`);
      setListening(false);
      recognitionRef.current = null;
    };
    rec.onend = () => {
      setListening(false);
      recognitionRef.current = null;
    };

    try {
      rec.start();
      recognitionRef.current = rec;
      setListening(true);
    } catch (err) {
      setError(`Could not start voice input: ${err.message}`);
      recognitionRef.current = null;
    }
  }, [supported, lang, Recognition]);

  // Cleanup on unmount.
  useEffect(() => () => {
    const rec = recognitionRef.current;
    if (rec) { try { rec.stop(); } catch { /* ignore */ } }
  }, []);

  const toggle = useCallback(() => {
    if (listening) stop(); else start();
  }, [listening, start, stop]);

  return { listening, supported, error, start, stop, toggle };
}
